import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastsStore } from 'react-toasts';
import { ResponseError } from 'superagent';
import { ApiClient } from 'src/client/helpers';
import urls, { constructUrl } from 'src/shared/urls';
import {
  ERROR_EMAIL_ALREADY_EXIST,
  STATUS_NOT_SENT,
  STATUS_OPTED_IN,
  STATUS_SENT,
} from 'src/shared/constants/newsletter';
import { Status } from 'src/client/components/forms/NewsletterForm';

const endpoint = constructUrl(urls.post.signup.newsletter);

type NewsletterState = {
  status: Status;
  submitted: boolean;
};

type NewsletterSubmit = {
  email?: string;
  type?: string;
};

const getInitialNewsletterState = (): NewsletterState => {
  const status = window.__INITIAL_STATE__?.initialState?.newsletter?.status as Status | undefined;
  return {
    status: (status || STATUS_NOT_SENT) as Status,
    submitted: false,
  };
};

const submitNewsletter = async (data: NewsletterSubmit) => {
  const response = await new ApiClient().post(endpoint, { data });
  return response.body as { error?: string };
};

export const useNewsletter = () => {
  const queryClient = useQueryClient();

  const { data } = useQuery<NewsletterState, ResponseError>(['newsletter-state'], getInitialNewsletterState, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const mutation = useMutation(submitNewsletter, {
    onSuccess: (result) => {
      const status = (result?.error === ERROR_EMAIL_ALREADY_EXIST ? STATUS_OPTED_IN : STATUS_SENT) as Status;
      ToastsStore.info('Subscribed to newsletter');
      queryClient.setQueryData<NewsletterState>(['newsletter-state'], {
        status,
        submitted: true,
      });
    },
    onError: () => {
      ToastsStore.info('Error subscribing to newsletter');
    },
  });

  return {
    status: (data?.status || STATUS_NOT_SENT) as Status,
    submitted: data?.submitted || false,
    submitting: mutation.isLoading,
    submitFailed: mutation.isError,
    submit: mutation.mutate,
  };
};
